import { useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { serviceDeleteMutation, serviceShowOptions } from '$api-client/@tanstack/react-query.gen'
import { PageLayout } from 'src/layouts/page'
import { EditIcon, BinIcon, FolderTagIcon } from '$components'
import { Card, Text, Button, Breadcrumb, Table, Tooltip, Skeleton, Alert } from '$components'
import { ElementIntent } from '$components'
import { createColumnHelper } from '@tanstack/react-table'
import { Role, ServiceValueVariant } from '$api-client/types.gen'
import { useMemo, useState } from 'react'
import { ChipCell } from '$components'
import { OptionsDropdown } from '$components'
import { GenericTable } from '$components'
import { ToastHelper } from '$components'
import { ToastType } from '$components'
import { ServiceRoutePath } from '../routes/protected'
import { AnimatedOutlet } from '$components'
import { Link } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { Router } from '$utils/router'
import { ConfirmationModal } from '$components'
import { useAuth } from '$features/auth/hooks/use-auth'
import { PermissionsHelper } from '$utils/permissions'
import { BreadcrumbSkeleton } from '$components'
import { InfoSkeleton } from '$components'
import { TableSkeleton } from '$components'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { PriceFormatter } from '$components'
import { TimeFormatter } from '$components'
import { usePageTitle } from '$components'

const columnHelper = createColumnHelper<ServiceValueVariant>()

export type ServiceDeleteConfirmationModalState = {
  isOpen: boolean,
  serviceId: number | null,
}

export function ServicePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { hasSubscriptionExpired } = useTenant()
  const [searchParams] = useSearchParams()

  const valuesId = searchParams.get('values_id') ? Number(searchParams.get('values_id')) : undefined

  const [serviceDeleteConfirmationModalState, setServiceDeleteConfirmationModalState] = useState<ServiceDeleteConfirmationModalState>({
    isOpen: false,
    serviceId: null,
  })

  const { data: service, isLoading, error } = useQuery({
    ...serviceShowOptions({
      path: {
        id: Number(id),
      },
      query: {
        include_deleted: true,
        values_id: valuesId,
      },
    }),
  })


  const isOutdated = !service?.data?.values?.is_latest

  const { mutate: deleteService, isPending: isDeletingService } = useMutation({
    ...serviceDeleteMutation(),
    onSuccess: () => {
      ToastHelper.deleteSuccess(ToastType.Service)
      setServiceDeleteConfirmationModalState({ isOpen: false, serviceId: null })
      navigate(ServiceRoutePath.Services)
    },
    onError: () => ToastHelper.deleteError(ToastType.Service),
  })

  const handleConfirmDeleteService = () => {
    deleteService({
      path: {
        id: Number(id),
      },
    })
  }

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'variant',
      header: 'Variant',
      cell: ({ row }) => {
        const variant = row.original
        let label: string
        if (variant.is_default) {
          label = 'Default'
        } else if (variant.breed) {
          label = variant.breed
        } else {
          label = `${variant.size_category?.values?.name ?? variant.size_category_id} / ${variant.coat_type?.values?.name ?? variant.coat_type_id}`
        }
        return <ChipCell values={[label]} />
      },
    }),
    columnHelper.accessor('duration', {
      header: 'Duration',
      cell: ({ getValue }) => <TimeFormatter>{getValue()}</TimeFormatter>,
    }),
    columnHelper.accessor('price_in_cents', {
      header: 'Price',
      cell: ({ getValue }) => (
        <PriceFormatter currency={service?.data?.values?.currency}>
          {getValue()}
        </PriceFormatter>
      ),
    }),
  ], [service?.data?.values?.currency])

  usePageTitle(service?.data?.values?.name || 'Service')

  if (isLoading) {
    return (
      <PageLayout>
        <div className="flex flex-col gap-stack-section">
          <BreadcrumbSkeleton />

          <div className="flex gap-stack-roomy items-center">
            <Skeleton
              width={40}
              height={40}
              borderRadius={16}
            />
            <Skeleton
              width={160}
              height={24}
              borderRadius={8}
            />
          </div>

          {/* Service Information Card Skeleton */}
          <Card
            rounding="xl"
            padding="none"
          >
            <Card.Header padding="sm">
              <Skeleton
                width={160}
                height={20}
                borderRadius={7}
              />
            </Card.Header>

            <Card.Content
              padding="sm"
              className="flex flex-col gap-stack-roomy"
            >
              <InfoSkeleton
                labelWidth={110}
                contentWidth={300}
              />
              <InfoSkeleton
                labelWidth={120}
                contentWidth={260}
              />
              <InfoSkeleton
                labelWidth={80}
                contentWidth={50}
              />
            </Card.Content>
          </Card>

          {/* Variants Section Skeleton */}
          <div className="flex flex-col gap-stack-roomy">
            <Skeleton
              width={120}
              height={20}
              borderRadius={7}
            />

            <TableSkeleton
              columns={3}
              rows={3}
              columnWidths={[120, 200, 160]}
            />
          </div>

          <AnimatedOutlet />
        </div>
      </PageLayout>
    )
  }

  if (error || !service) {
    return (
      <PageLayout>
        <div className="flex flex-col items-center justify-center h-full">
          <Text
            as="h2"
            className="text-danger"
          >Error loading service</Text>
          <Text>{error?.message || 'Service not found'}</Text>
        </div>
      </PageLayout>
    )
  }

  const isEditDisabled = hasSubscriptionExpired || PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN]) || isOutdated
  const isPricingEditDisabled = hasSubscriptionExpired || PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN]) || isOutdated

  const getEditTooltipContent = () => {
    if (hasSubscriptionExpired) {return SUBSCRIPTION_EXPIRED_MESSAGE}
    if (PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])) {return 'You are not allowed to edit services.'}
    if (isOutdated) {return 'You cannot edit old services. Navigate to the latest version of this service to edit.'}
    return undefined
  }

  return (
    <PageLayout>
      <div className="flex flex-col gap-stack-section pb-stack-roomy">
        {isOutdated && (
          <Alert intent={ElementIntent.Warning}>
            <Alert.Heading>
              You are viewing an old version of this service
            </Alert.Heading>

            <Alert.Body>
              To view the latest version, please click&nbsp;
              <Link
                to={Router.getRoute(ServiceRoutePath.ServicesView, { id: service.data.id })}
                className="hover:text-warning-fg underline"
              >
                here
              </Link>
              .
            </Alert.Body>
          </Alert>
        )}

        <Breadcrumb
          items={[
            {
              path: ServiceRoutePath.Services,
              label: 'Services',
              icon: FolderTagIcon,
            },
            {
              path: ServiceRoutePath.ServicesView,
              label: service.data.values!.name,
            },
          ]}
        />

        <div className="flex items-center justify-between">
          <div className="flex gap-stack-roomy items-center">
            <div
              className="size-10 rounded-card-sm"
              style={{ backgroundColor: service.data.colour }}
            />

            <Text as="h1">
              { service.data.values?.name }
            </Text>

            <Tooltip
              content={getEditTooltipContent()}
              disabled={!isEditDisabled}
            >
              <Link to={ServiceRoutePath.ServicesViewEdit}>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={EditIcon}
                  disabled={isEditDisabled}
                >
                  Edit
                </Button>
              </Link>
            </Tooltip>
          </div>

          <OptionsDropdown options={[
            {
              id: 'delete',
              value: 'Delete',
              onClick: () => setServiceDeleteConfirmationModalState({ isOpen: true, serviceId: service.data.id }),
              intent: ElementIntent.Danger,
              icon: BinIcon,
              disabled: !!service.data.deleted_at || PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN]),
              tooltip: service.data.deleted_at
                ? 'This service has already been deleted.'
                : PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
                  ? 'You are not allowed to delete services.'
                  : undefined,
            },
          ]}
          />
        </div>

        {/* Service information section */}
        <Card
          rounding="xl"
          padding="none"
        >
          <Card.Header
            padding="sm"
            className="flex justify-between items-center"
          >
            <Text type="h4">Service Information</Text>

            <Tooltip
              content={getEditTooltipContent()}
              disabled={!isEditDisabled}
            >
              <Link to={ServiceRoutePath.ServicesViewEditInfo}>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={isEditDisabled}
                  icon={EditIcon}
                >
                  Edit
                </Button>
              </Link>
            </Tooltip>
          </Card.Header>

          <Card.Content
            padding="sm"
            className="flex flex-col gap-stack-section"
          >
            <div>
              <Card.Label>Description</Card.Label>
              {service.data.values?.description
                ? (
                  <Text type="s">{service.data.values.description}</Text>
                )
                : (
                  <Text type="s" className="text-fg-subtle italic">No description provided</Text>
                )}
            </div>

            <div>
              <Card.Label>Internal Notes</Card.Label>
              {service.data.values?.notes
                ? (
                  <Text type="s">{service.data.values.notes}</Text>
                )
                : (
                  <Text type="s" className="text-fg-subtle italic">No internal notes</Text>
                )}
            </div>

            <div>
              <Card.Label>Currency</Card.Label>
              <Text type="s">{service.data.values!.currency}</Text>
            </div>
          </Card.Content>
        </Card>

        {/* Variants section */}
        <div className="flex justify-between">
          <Text type="h3">
            Pricing
          </Text>

          <Tooltip
            content={getEditTooltipContent()}
            disabled={!isPricingEditDisabled}
          >
            <Link to={ServiceRoutePath.ServicesViewPricingEdit}>
              <Button
                variant="secondary"
                size="sm"
                icon={EditIcon}
                disabled={isPricingEditDisabled}
              >
                Edit Pricing
              </Button>
            </Link>
          </Tooltip>
        </div>

        <Table.Container>
          <GenericTable
            data={service.data.values?.variants ?? []}
            columns={columns}
          />
        </Table.Container>
      </div>

      <AnimatedOutlet />

      {serviceDeleteConfirmationModalState.isOpen && (
        <ConfirmationModal
          isLoading={isDeletingService}
          intent={ElementIntent.Danger}
          onClose={() => setServiceDeleteConfirmationModalState({ isOpen: false, serviceId: null })}
          onConfirm={handleConfirmDeleteService}
          title="Delete Service"
          description="Are you sure you want to delete this service?"
        />
      )}
    </PageLayout>
  )
}
